import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Chip,
} from '@mui/joy';
import {
  ArrowCounterClockwise as UndoIcon,
  ArrowClockwise as RedoIcon,
  ClockCounterClockwise as HistoryIcon,
} from '@phosphor-icons/react';

interface UndoRedoControlsProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  historyInfo?: {
    undoSteps: number;
    redoSteps: number;
  };
  showHistory?: boolean;
  onShowHistory?: () => void;
}

export const UndoRedoControls: React.FC<UndoRedoControlsProps> = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  historyInfo,
  showHistory = false,
  onShowHistory,
}) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip
        title={
          <Box>
            <Typography level="body-sm" fontWeight="bold">
              Undo
            </Typography>
            <Typography level="body-xs">
              {cmdKey}+Z
            </Typography>
            {historyInfo && historyInfo.undoSteps > 0 && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                {historyInfo.undoSteps} steps available
              </Typography>
            )}
          </Box>
        }
        placement="bottom"
      >
        <IconButton
          size="sm"
          variant="plain"
          disabled={!canUndo}
          onClick={onUndo}
        >
          <UndoIcon />
        </IconButton>
      </Tooltip>

      <Tooltip
        title={
          <Box>
            <Typography level="body-sm" fontWeight="bold">
              Redo
            </Typography>
            <Typography level="body-xs">
              {cmdKey}+{isMac ? 'Shift+Z' : 'Y'}
            </Typography>
            {historyInfo && historyInfo.redoSteps > 0 && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                {historyInfo.redoSteps} steps available
              </Typography>
            )}
          </Box>
        }
        placement="bottom"
      >
        <IconButton
          size="sm"
          variant="plain"
          disabled={!canRedo}
          onClick={onRedo}
        >
          <RedoIcon />
        </IconButton>
      </Tooltip>

      {showHistory && onShowHistory && (
        <Tooltip title="View history" placement="bottom">
          <IconButton
            size="sm"
            variant="plain"
            onClick={onShowHistory}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      )}

      {historyInfo && (canUndo || canRedo) && (
        <Chip
          size="sm"
          variant="soft"
          color="neutral"
          sx={{ ml: 1 }}
        >
          {historyInfo.undoSteps + historyInfo.redoSteps + 1} changes
        </Chip>
      )}
    </Box>
  );
};
